import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useRef, useState } from "react";
import { Outlet } from "react-router-dom";
import axios, { AxiosResponse } from "axios";
import { API_CONFIG } from "@/constants/api";
import { STORAGE_KEYS } from "@/constants/constants";
import { UI_MESSAGES } from "@/constants/ui-constants";
import { env } from "@/lib/env";
import { AXIOS_INSTANCE as axiosInstance, setGetIdTokenClaims } from "@/lib/axios-instance";
import { AuthExchangeResponse } from "@/lib/api/types";


const AuthLayout = () => {
  const {
    isAuthenticated,
    loginWithRedirect,
    logout,
    isLoading,
    getIdTokenClaims,
  } = useAuth0();

  // Prevent duplicate exchanges while a request is in flight
  const exchangingRef = useRef(false);
  
  // State to track authentication errors
  const [authError, setAuthError] = useState<string | null>(null);
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  // Register getIdTokenClaims with the axios interceptor so it can silently
  // refresh the Auth0 token before it expires, rather than reading a stale
  // value from sessionStorage.
  useEffect(() => {
    setGetIdTokenClaims(async () => {
      const claims = await getIdTokenClaims();
      return claims ? { __raw: claims.__raw, exp: claims.exp } : undefined;
    });
  }, [getIdTokenClaims]);

  // Function to clear session data and logout
  const clearSessionAndLogout = () => {
    // Clear all session storage
    sessionStorage.removeItem(STORAGE_KEYS.AUTH0_RAW_TOKEN);
    sessionStorage.removeItem(STORAGE_KEYS.USER_PROFILE);
    sessionStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    
    // Logout from Auth0
    logout({
      logoutParams: {
        returnTo: window.location.origin
      }
    });
  };

  // If not authenticated, redirect to Auth0 login
  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      loginWithRedirect();
    }
  }, [isAuthenticated, isLoading, loginWithRedirect]);

  /**
   * Once authenticated:
   * 1) Get Auth0 ID token and store it
   * 2) Call /api/auth/exchange to get user profile data (role, authorities, etc.)
   * 3) Store user profile in sessionStorage
   * 4) Use Auth0 token for all subsequent API calls
   */

  useEffect(() => {
    let logoutTimer: ReturnType<typeof setTimeout>;

    const exchangeIfNeeded = async () => {
      if (!isAuthenticated) return;
      if (exchangingRef.current) return;

      // Check if we already have Auth0 token and user profile
      const existingAuth0Token = sessionStorage.getItem(STORAGE_KEYS.AUTH0_RAW_TOKEN);
      const existingProfile = sessionStorage.getItem(STORAGE_KEYS.USER_PROFILE);
      
      if (existingAuth0Token && existingProfile) {
        // Already have everything we need
        return;
      }

      exchangingRef.current = true;
      setIsAuthenticating(true);
      setAuthError(null);
      
      try {
        // 1) Get Auth0 ID token
        const idTokenClaims = await getIdTokenClaims();
        const auth0Token = idTokenClaims?.__raw;

        if (!auth0Token) {
          throw new Error(UI_MESSAGES.FAILED_GET_ID_TOKEN);
        }

        // Store the Auth0 token for API requests
        sessionStorage.setItem(STORAGE_KEYS.AUTH0_RAW_TOKEN, auth0Token);

        // 2) Exchange to get user profile data (role, authorities, etc.)
        const resp: AxiosResponse<AuthExchangeResponse> = await axiosInstance.post(
          API_CONFIG.ENDPOINTS.AUTH_EXCHANGE,
          {},
          {
            headers: { Authorization: `Bearer ${auth0Token}` },
            withCredentials: true,
          }
        );

        const data = resp.data;
        
        // Validate response
        if (!data.role || !data.username) {
          throw new Error('Invalid response from authentication server');
        }
        
        // Store user profile data (without the token since we use Auth0 token)
        const userProfile = {
          firstName: data.firstName,
          lastName: data.lastName,
          fullName: data.fullName ?? [data.firstName, data.lastName].filter(Boolean).join(' '),
          username: data.username,
          role: data.role,
          authorities: data.authorities,
        };
        
        const existingProfileData = sessionStorage.getItem(STORAGE_KEYS.USER_PROFILE);
        const newProfileData = JSON.stringify(userProfile);
        
        // Only update and dispatch event if profile actually changed
        if (existingProfileData !== newProfileData) {
          sessionStorage.setItem(STORAGE_KEYS.USER_PROFILE, newProfileData);
          window.dispatchEvent(new Event('sessionStorageChange'));
        } else {
        }
        
        exchangingRef.current = false;
        setIsAuthenticating(false);
      } catch (err) {
        
        // Handle different types of errors
        let errorMessage: string = UI_MESSAGES.ACCESS_DENIED;
        
        if (axios.isAxiosError(err)) {
          // Handle HTTP errors
          if (err.response?.status === 401 || err.response?.status === 403) {
            errorMessage = UI_MESSAGES.ACCESS_DENIED;
          } else if (err.response && err.response.status >= 500) {
            errorMessage = UI_MESSAGES.ACCESS_DENIED; // Use standard message for all auth failures
          } else if (!err.response) {
            // Network error - provide more helpful message
            errorMessage = `Network error: Unable to connect to ${env.API_BASE_URL}. Please check your connection and ensure the backend is running.`;
          }
        }
        
        setAuthError(errorMessage);
        
        // Clear any existing session data
        sessionStorage.removeItem(STORAGE_KEYS.AUTH0_RAW_TOKEN);
        sessionStorage.removeItem(STORAGE_KEYS.USER_PROFILE);
        
        // Auto-logout after a short delay to show the error message.
        // Keep exchangingRef.current = true until the timer fires so no
        // re-entry into the exchange flow is possible during the error window.
        logoutTimer = setTimeout(() => {
          exchangingRef.current = false;
          clearSessionAndLogout();
        }, 3000);
      }
    };

    exchangeIfNeeded();

    return () => clearTimeout(logoutTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]); // Only re-run when authentication status changes

  // Show loading state
  if (isLoading || isAuthenticating) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">
            {isLoading ? "Loading..." : "Authenticating..."}
          </p>
        </div>
      </div>
    );
  }

  // Show authentication error
  if (authError) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6 text-center">
          <div className="mb-4">
            <svg className="mx-auto h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-6">{authError}</p>
          <div className="text-sm text-gray-500">
            Redirecting to login page...
          </div>
        </div>
      </div>
    );
  }

  return isAuthenticated ? <Outlet /> : <div>Loading...</div>;
};

export default AuthLayout;
